import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fmtDateTime, TABLE_HEAD_ROW_SX } from '../../Utils/format';
import useCustomAuth from '../../Context/Hooks/useCustomAuth';
import ClientBadge from './ClientBadge';
import {
  Box,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const matchesSearch = (device, q) => {
  if (!q) return true;
  const lq = q.toLowerCase();
  return (
    (device.name ?? '').toLowerCase().includes(lq) ||
    (device.serialNumber ?? '').toLowerCase().includes(lq) ||
    (String(device.id ?? '')).toLowerCase().includes(lq) ||
    (device.zone ?? '').toLowerCase().includes(lq) ||
    (device.storeName ?? '').toLowerCase().includes(lq) ||
    (device.clientName ?? '').toLowerCase().includes(lq)
  );
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const { t } = useTranslation('devices');
  const STATUS_CONFIG = {
    ONLINE: { label: t('status.ONLINE', 'Online'), bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', dot: '#16a34a' },
    WARNING: { label: t('status.WARNING', 'Aviso'), bg: '#fffaf0', color: '#d98c1f', border: '#f7e6c4', dot: '#f59e0b' },
    DEGRADED: { label: t('status.WARNING', 'Aviso'), bg: '#fffaf0', color: '#d98c1f', border: '#f7e6c4', dot: '#f59e0b' },
    OFFLINE: { label: t('status.OFFLINE', 'Offline'), bg: '#fef2f2', color: '#e2453b', border: '#fadcd9', dot: '#e2453b' },
    ERROR: { label: t('status.OFFLINE', 'Offline'), bg: '#fef2f2', color: '#e2453b', border: '#fadcd9', dot: '#e2453b' },
  };

  const cfg = STATUS_CONFIG[status?.toUpperCase()] ?? STATUS_CONFIG.OFFLINE;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        pl: '8px',
        pr: '10px',
        py: '3px',
        borderRadius: '999px',
        bgcolor: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        fontSize: 12.5,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </Box>
  );
};

StatusBadge.propTypes = { status: PropTypes.string };

// ─── Shared styles ────────────────────────────────────────────────────────────

const SELECT_SX = {
  fontSize: 13,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
};

// ─── Component ────────────────────────────────────────────────────────────────

const DevicesTable = ({ devices = [] }) => {
  const { t } = useTranslation(['devices', 'common']);
  const { user: authUser } = useCustomAuth();
  const currentUserRole = authUser?.role ?? 'USER';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const STATUS_OPTIONS = [
    { value: '', label: t('common:labels.all', 'Todos') },
    { value: 'ONLINE', label: t('devices:status.ONLINE', 'Online') },
    { value: 'WARNING', label: t('devices:status.WARNING', 'Aviso') },
    { value: 'OFFLINE', label: t('devices:status.OFFLINE', 'Offline') },
  ];

  const filtered = devices.filter(
    (d) =>
      matchesSearch(d, search) &&
      (!statusFilter || d.status?.toUpperCase() === statusFilter),
  );

  const COLUMNS = [
    t('devices:table.device', 'Dispositivo'),
    t('devices:table.storeClient', 'Loja · Cliente'),
    t('devices:table.status', 'Estado'),
    t('devices:table.lastSeen', 'Último contacto'),
    t('devices:table.firmware', 'Firmware'),
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
      }}
    >
      {/* ── Toolbar ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TextField
            size="small"
            placeholder={t('common:tableActions.search', 'Pesquisar…')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 220,
              '& .MuiInputBase-root': { fontSize: 13 },
              '& .MuiOutlinedInput-root fieldset': { borderColor: 'divider' },
            }}
          />

          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            sx={{ ...SELECT_SX, minWidth: 150 }}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem
                key={o.value}
                value={o.value}
                sx={{ fontSize: 13 }}
              >{o.label}</MenuItem>
            ))}
          </Select>
        </Box>

        <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {t('devices:filters.count', { count: filtered.length, defaultValue: `${filtered.length} devices` })}
        </Typography>
      </Box>

      {/* ── Table ── */}
      {filtered.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography
            variant="body2"
            color="text.disabled"
          >{t('devices:table.empty', 'Nenhum dispositivo encontrado.')}</Typography>
        </Box>
      ) : (
        <Table sx={{ '& .MuiTableCell-root': { fontSize: 13, borderColor: 'divider' } }}>
          <TableHead>
            <TableRow sx={TABLE_HEAD_ROW_SX}>
              {COLUMNS.map((h, i) => (
                <TableCell
                  key={h}
                  sx={{
                    ...(i === 0 && { pl: 2.5 }),
                    ...(i === COLUMNS.length - 1 && { pr: 1.5 }),
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((device) => (
              <TableRow
                key={device.id}
                sx={{
                  '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
                  '&:hover': { bgcolor: '#f9fafb' },
                }}
              >
                <TableCell sx={{ pl: 2.5 }}>
                  {device.deviceId || device.id ? (
                    <Link
                      component={RouterLink}
                      to={`/Gateway/Devices/${device.deviceId || device.id}`}
                      underline="hover"
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'primary.main',
                        display: 'block',
                        lineHeight: 1.3,
                      }}
                    >
                      {device.name || device.serialNumber || device.id}
                    </Link>
                  ) : (
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary', display: 'block', lineHeight: 1.3 }}>
                      {device.name || device.serialNumber || device.id}
                    </Typography>
                  )}
                  {(device.zone || (device.serialNumber && device.serialNumber !== device.name)) && (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: '2px' }}>
                      {[device.zone, device.serialNumber && device.serialNumber !== device.name ? device.serialNumber : null].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  {device.storeId && device.storeName && device.storeName !== '—' ? (
                    <Link
                      component={RouterLink}
                      to={`/Gateway/Stores/${device.storeId}`}
                      underline="hover"
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'primary.main',
                        display: 'block',
                        lineHeight: 1.3,
                      }}
                    >
                      {device.storeName}
                    </Link>
                  ) : (
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: device.storeName && device.storeName !== '—' ? 'text.primary' : 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                      {device.storeName && device.storeName !== '—' ? device.storeName : '—'}
                    </Typography>
                  )}
                  {device.clientName && device.clientName !== '—' && (
                    (currentUserRole === 'GATEWAY_ADMIN' || currentUserRole === 'CLIENT_ADMIN') && device.clientId ? (
                      <Link
                        component={RouterLink}
                        to={`/Gateway/Clients/${device.clientId}`}
                        underline="none"
                        sx={{ display: 'inline-block', mt: 0.5 }}
                      >
                        <ClientBadge clientName={device.clientName} />
                      </Link>
                    ) : (
                      <Box sx={{ display: 'inline-block', mt: 0.5 }}>
                        <ClientBadge clientName={device.clientName} />
                      </Box>
                    )
                  )}
                </TableCell>

                <TableCell>
                  <StatusBadge status={device.status} />
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: 'text.secondary' }}>
                    {fmtDateTime(device.lastSeen)}
                  </Typography>
                </TableCell>

                <TableCell sx={{ pr: 1.5 }}>
                  <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: 'text.secondary' }}>
                    {device.firmware || '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

DevicesTable.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.object),
};

export default DevicesTable;
